import React, { useCallback, useEffect, useRef, useState, type FC } from 'react';

import { faUsd } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios from 'axios';

import { Alert, Button, Modal, Text } from '@shared/components/bootstrap';
import { Currency } from '@shared/components/helpers';

import type { IFullInvoice, IInvoiceRow } from './types';

export interface InvoicePayModalProps {
  invoice: IFullInvoice | IInvoiceRow;
  onClose(): void;
  onComplete(): void;
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const MAX_POLL_ATTEMPTS = 12;

const tomorrowInputMin = (): string => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const invoiceCurrencyCode = (currency: string | undefined): 'USD' | 'CAD' => {
  const upper = (currency || 'usd').toUpperCase();
  return upper === 'CAD' ? 'CAD' : 'USD';
};

export const InvoicePayModal: FC<InvoicePayModalProps> = ({ invoice, onClose, onComplete }) => {
  const [amountToPay, setAmountToPay] = useState(String(invoice.amount_owed));
  const [deferDate, setDeferDate] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<string | undefined>();
  const pollCancelledRef = useRef(false);

  useEffect(() => {
    pollCancelledRef.current = false;
    return () => {
      pollCancelledRef.current = true;
    };
  }, []);

  const handleClose = useCallback(() => {
    pollCancelledRef.current = true;
    onClose();
  }, [onClose]);

  const displayCurrency = 'currency' in invoice ? invoiceCurrencyCode(invoice.currency) : undefined;

  const deferredBalance = () => Number(invoice.amount_owed) - Number(amountToPay);

  const dateRequired = () => {
    if (deferredBalance() > 0) {
      return deferDate !== '';
    }
    return true;
  };

  const canSubmit = () => deferredBalance() >= 0 && dateRequired() && notes.length > 0 && !loading;

  const pollFind = async (attemptID: string) => {
    const accountID = invoice.account_id;
    let polling = true;
    let pollAttempts = 0;
    while (polling) {
      if (pollCancelledRef.current) return;
      try {
        const { data, status } = await axios.get(`/accounts/${accountID}/invoices/find.json`, {
          params: { attempt_id: attemptID },
        });
        if (pollCancelledRef.current) return;
        if (status === 200 && data.message) {
          setErrors(String(data.message));
          setLoading(false);
          polling = false;
          return;
        }
        setLoading(false);
        onComplete();
        polling = false;
        return;
      } catch (e: unknown) {
        if (pollCancelledRef.current) return;
        const err = e as { response?: { status?: number } };
        if (err.response?.status === 404) {
          pollAttempts += 1;
          if (pollAttempts > MAX_POLL_ATTEMPTS) {
            if (pollCancelledRef.current) return;
            setLoading(false);
            setErrors('Payment polling timed out. Please try again or contact support.');
            polling = false;
            return;
          }
          await sleep(5000);
          if (pollCancelledRef.current) return;
        } else {
          if (pollCancelledRef.current) return;
          setLoading(false);
          setErrors('Payment polling failed. Please refresh the page.');
          polling = false;
        }
      }
    }
  };

  const onPay = async () => {
    if (!canSubmit()) return;

    const amount = parseFloat(amountToPay);
    if (!Number.isFinite(amount) || amount <= 0) {
      setErrors('Enter a valid payment amount greater than zero.');
      return;
    }
    const balance = Number(invoice.amount_owed);
    if (!Number.isFinite(balance)) {
      setErrors('Unable to read invoice balance. Please refresh the page.');
      return;
    }
    if (amount > balance) {
      setErrors('Payment amount cannot exceed the amount due.');
      return;
    }

    setLoading(true);
    setErrors(undefined);
    try {
      const payload: { amount: string; notes: string; date?: string | null } = {
        amount: amountToPay,
        notes,
      };
      const amountCents = Math.round(amount * 100);
      const balanceCents = Math.round(balance * 100);
      const isPartialPayment = amountCents < balanceCents;
      if (isPartialPayment && deferDate) {
        payload.date = deferDate;
      }

      const { data } = await axios.post<{ attempt_id: string }>(
        `/accounts/${invoice.account_id}/invoices/${invoice.id}/pay.json`,
        payload,
      );
      if (pollCancelledRef.current) return;
      await pollFind(data.attempt_id);
    } catch (e: unknown) {
      if (pollCancelledRef.current) return;
      const err = e as { response?: { data?: { message?: string } } };
      setLoading(false);
      setErrors(err.response?.data?.message ?? 'Payment failed.');
    }
  };

  return (
    <Modal centered onClose={handleClose}>
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>Pay Invoice: #{invoice.id}</Modal.Title>
          <Text tag="p">
            Total Due: <Currency value={invoice.amount_owed} currency={displayCurrency} />
          </Text>
          <Modal.Close close={handleClose} />
        </Modal.Header>
        <Modal.Body>
          {loading && <div className="spinner">Loading</div>}
          {errors && (
            <Alert style="danger">
              <strong>Error: </strong>
              {errors}
            </Alert>
          )}
          <div className="row">
            <label className="control-label" htmlFor="amountToPayInput">
              Amount to pay
            </label>
            <div className="input-group">
              <div className="input-group-addon">
                <FontAwesomeIcon icon={faUsd} />
              </div>
              <input
                id="amountToPayInput"
                className="form-control"
                type="number"
                step="0.01"
                min="0"
                value={amountToPay}
                onChange={(e) => setAmountToPay(e.target.value)}
              />
            </div>
            <label className="control-label mar-top" htmlFor="deferDateInput">
              Defer until
            </label>
            <div className="input-group">
              <div className="input-group-addon" id="deferDateInput-addon">
                Defer <Currency value={deferredBalance()} currency={displayCurrency} /> Until
              </div>
              <input
                id="deferDateInput"
                className="form-control"
                type="date"
                value={deferDate}
                min={tomorrowInputMin()}
                onChange={(e) => setDeferDate(e.target.value)}
              />
            </div>
            <label className="control-label mar-top" htmlFor="notesInput">
              Notes
            </label>
            <input
              id="notesInput"
              className="form-control mar-top mar-btm"
              placeholder="Notes"
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button kind="danger" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button kind="primary" onClick={() => void onPay()} disabled={!canSubmit()}>
            Pay
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
};
